"use client"

import { useState } from "react"
import { ChevronRight, Eye, EyeOff } from "lucide-react"
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete"
import { FONT_HEADING, FONT_SANS } from "@/lib/constants"
import type { RdvFlow } from "../_hooks/useRdvFlow"
import { TYPES } from "../_types"
import { TextField, errorTextStyle, inputStyle, labelStyle } from "./fields"

export function Step1Informations({ flow }: { flow: RdvFlow }) {
  const { isPro, switchProfile, form, errors, set, goToStep2 } = flow
  const [showAccessCode, setShowAccessCode] = useState(false)

  return (
    <div
      style={{
        background: "var(--ljs-surface)",
        border: "1px solid var(--ljs-border)",
        borderRadius: 16,
        padding: 28,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <h1
        style={{
          fontFamily: FONT_HEADING,
          fontSize: 20,
          fontWeight: 700,
          color: "var(--ljs-text)",
          margin: 0,
        }}
      >
        Vos informations
      </h1>

      {/* Toggle particulier / professionnel */}
      <div style={{ display: "flex", gap: 8 }}>
        {(["Particulier", "Professionnel"] as const).map((label) => {
          const active = label === "Professionnel" ? isPro : !isPro
          return (
            <button
              key={label}
              type="button"
              onClick={() => switchProfile(label === "Professionnel")}
              style={{
                flex: 1,
                padding: "8px 0",
                borderRadius: 8,
                border: `1.5px solid ${active ? "#0EA5E9" : "var(--ljs-border)"}`,
                background: active ? "rgba(14,165,233,0.08)" : "var(--ljs-surface)",
                color: active ? "#0EA5E9" : "var(--ljs-text-muted)",
                fontFamily: FONT_SANS,
                fontSize: 13,
                fontWeight: active ? 700 : 400,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Identité */}
      {isPro ? (
        <TextField
          label="Raison sociale *"
          value={form.companyName}
          onChange={(v) => set("companyName", v)}
          placeholder="ACME Immobilier"
          error={errors.companyName}
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 14,
          }}
        >
          <TextField
            label="Prénom *"
            value={form.firstName}
            onChange={(v) => set("firstName", v)}
            placeholder="Jean"
            error={errors.firstName}
          />
          <TextField
            label="Nom *"
            value={form.lastName}
            onChange={(v) => set("lastName", v)}
            placeholder="Dupont"
            error={errors.lastName}
          />
        </div>
      )}

      <TextField
        label="Téléphone *"
        type="tel"
        value={form.phone}
        onChange={(v) => set("phone", v)}
        placeholder="06 12 34 56 78"
        error={errors.phone}
        hint="La confirmation de RDV sera envoyée par SMS"
      />

      <TextField
        label="Email (optionnel)"
        type="email"
        value={form.email}
        onChange={(v) => set("email", v)}
        placeholder="jean.dupont@email.com"
      />

      {/* Adresse */}
      <div>
        <label style={labelStyle}>{"Adresse *"}</label>
        <AddressAutocomplete
          value={form.address}
          onChange={(v) => set("address", v)}
          onSelect={({ street, postalCode, city }) => {
            set("address", street)
            if (postalCode) set("postalCode", postalCode)
            if (city) set("city", city)
          }}
          error={!!errors.address}
          variant="inline"
          placeholder="Adresse"
        />
        {errors.address && <p style={errorTextStyle}>{errors.address}</p>}
      </div>

      <TextField
        label="Résidence"
        value={form.residenceName}
        onChange={(v) => set("residenceName", v)}
        placeholder="Ex: Résidence Les Lilas"
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TextField
          label={"Bât / Entrée / Escalier"}
          value={form.bat}
          onChange={(v) => set("bat", v)}
          placeholder="Ex: Bât A"
        />
        <TextField
          label="Étage"
          value={form.floor}
          onChange={(v) => set("floor", v)}
          placeholder="Ex: 3ème droite"
          autoComplete="off"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TextField
          label="Code postal"
          value={form.postalCode}
          onChange={(v) => set("postalCode", v)}
          placeholder="75001"
        />
        <TextField
          label="Ville"
          value={form.city}
          onChange={(v) => set("city", v)}
          placeholder="Paris"
        />
      </div>

      {/* Code d'accès — champ masquable */}
      <div>
        <label style={labelStyle}>Code d{"'"}accès</label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            style={
              {
                ...inputStyle,
                paddingRight: 44,
                WebkitTextSecurity: showAccessCode ? "none" : "disc",
              } as React.CSSProperties
            }
            value={form.accessCode}
            onChange={(e) => set("accessCode", e.target.value)}
            placeholder="Code immeuble"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowAccessCode((v) => !v)}
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--ljs-text-faint)",
              padding: 4,
              display: "flex",
              alignItems: "center",
            }}
            tabIndex={-1}
            aria-label={showAccessCode ? "Masquer le code" : "Afficher le code"}
          >
            {showAccessCode ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {/* Type d'intervention */}
      <div>
        <label style={{ ...labelStyle, marginBottom: 10 }}>
          {"Type d'intervention *"}
          {errors.type && (
            <span style={{ color: "#EF4444", fontWeight: 400, marginLeft: 8 }}>{errors.type}</span>
          )}
        </label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: 10,
          }}
        >
          {TYPES.map((t) => {
            const active = form.type === t.value
            return (
              <button
                key={t.value}
                onClick={() => set("type", t.value)}
                style={{
                  padding: "12px 10px",
                  borderRadius: 10,
                  border: active ? "2px solid #0EA5E9" : "1px solid var(--ljs-border)",
                  background: active ? "rgba(14,165,233,0.06)" : "var(--ljs-surface)",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 20 }}>{t.icon}</span>
                <span
                  style={{
                    fontFamily: FONT_SANS,
                    fontSize: 13,
                    fontWeight: 600,
                    color: active ? "#0EA5E9" : "var(--ljs-text)",
                  }}
                >
                  {t.label}
                </span>
                <span style={{ fontSize: 11, color: "var(--ljs-text-faint)" }}>{t.desc}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Description */}
      <div>
        <label style={labelStyle}>Description du problème (optionnel)</label>
        <textarea
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          rows={3}
          placeholder="Décrivez brièvement le problème rencontré..."
          style={{
            ...inputStyle,
            resize: "vertical",
            lineHeight: 1.6,
            minHeight: 80,
          }}
        />
      </div>

      {/* Consentement RGPD */}
      <div
        style={{
          background: "var(--ljs-bg)",
          border: `1px solid ${errors.consent ? "#EF4444" : "var(--ljs-border)"}`,
          borderRadius: 10,
          padding: "14px 16px",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => set("consent", e.target.checked)}
            style={{ marginTop: 2, accentColor: "#0EA5E9", flexShrink: 0 }}
          />
          <span style={{ fontSize: 12, color: "var(--ljs-text-dim)", lineHeight: 1.6 }}>
            {
              "J'ai pris connaissance que LJS Plomberie utilisera mes coordonnées (nom, téléphone, adresse) pour la gestion de ce rendez-vous et l'envoi des notifications associées (confirmation, rappel, annulation), conformément à notre "
            }
            <span style={{ color: "#0EA5E9", textDecoration: "underline", cursor: "pointer" }}>
              politique de confidentialité
            </span>
            {". Je peux exercer mes droits (accès, rectification, suppression) à tout moment."}
          </span>
        </label>
        {errors.consent && (
          <p style={{ fontSize: 11, color: "#EF4444", margin: "6px 0 0" }}>
            Vous devez accepter pour continuer
          </p>
        )}
      </div>

      <button
        onClick={goToStep2}
        style={{
          background: "#0EA5E9",
          color: "#fff",
          fontFamily: FONT_SANS,
          fontSize: 15,
          fontWeight: 700,
          padding: "13px 0",
          borderRadius: 10,
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        Continuer <ChevronRight size={18} />
      </button>
    </div>
  )
}
